// Janela "Folga ou férias" (13/10/2026).
//
// Pedido do Lucas: "Preciso que o pessoal possa anotar quando está de folga ou
// férias. Antes tinha na visualização de quadro. Agora ficou sem." A ausência
// por dia nunca saiu do sistema, mas depois que a grade saiu (30/09) ela virou
// um ícone sem nome no cabeçalho de cada dia — só para dia que já passou, um
// por vez. Aqui a pessoa escolhe o motivo e o PERÍODO (de–até, inclusive no
// futuro) e marca de uma vez; ao lado, o que já está marcado, com desfazer.
//
// Quem decide os dias é o servidor (POST /financeiro/horas/ausencias): só dia
// útil, sem hora lançada, sem reescrever o que já era ausência. O que o RH
// lançou (férias da ficha) aparece na lista, mas não se desfaz por aqui.
import { useCallback, useEffect, useState } from 'react';
import { Alert, App as AntApp, Button, Col, DatePicker, Input, Modal, Popconfirm, Radio, Row, Spin, Tag, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import {
  api, diasDeSemanaNoPeriodo, frasesDaMarcacao, periodoEmTexto, rotuloAusencia, TIPOS_AUSENCIA,
  type AusenciaMarcada, type RespostaAusencia,
} from './api';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type Props = {
  aberto: boolean;
  /** Hoje em São Paulo ('YYYY-MM-DD'), como o servidor informou. */
  hoje: string;
  /** Marcando no nome de outra pessoa (painel da equipe, ficha do RH). */
  emailAlvo?: string;
  onFechar: () => void;
  /** Algo foi marcado ou desfeito: a tela de trás relê a semana. */
  onMudou: () => void;
};

export default function ModalAusencia({ aberto, hoje, emailAlvo, onFechar, onMudou }: Props) {
  const { message } = AntApp.useApp();
  const [tipo, setTipo] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<[Dayjs, Dayjs]>(() => [dayjs(hoje), dayjs(hoje)]);
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [marcadas, setMarcadas] = useState<AusenciaMarcada[] | null>(null);
  const [erroLista, setErroLista] = useState<string | null>(null);

  const quem = emailAlvo ? { usuario_email: emailAlvo } : {};
  const carregarLista = useCallback(async () => {
    setErroLista(null);
    try {
      const d = await api(`/financeiro/horas/ausencias${emailAlvo ? `?email=${encodeURIComponent(emailAlvo)}` : ''}`);
      setMarcadas(d.ausencias || []);
    } catch (e: any) {
      // Não consegui ler ≠ não há ausência: a lista fica sem resposta, com o motivo.
      setMarcadas(null);
      setErroLista(e?.message || 'Não foi possível ler as ausências marcadas.');
    }
  }, [emailAlvo]);

  // A cada abertura: formulário limpo (hoje) e a lista de novo.
  useEffect(() => {
    if (!aberto) return;
    setTipo(null); setObservacao(''); setErro(null);
    setPeriodo([dayjs(hoje), dayjs(hoje)]);
    carregarLista();
  }, [aberto, hoje, carregarLista]);

  const inicio = periodo[0].format('YYYY-MM-DD');
  const fim = periodo[1].format('YYYY-MM-DD');
  const diasDeSemana = diasDeSemanaNoPeriodo(inicio, fim);

  async function marcar() {
    if (!tipo) { setErro('Escolha o motivo.'); return; }
    setEnviando(true); setErro(null);
    try {
      const r: RespostaAusencia = await api('/financeiro/horas/ausencias', {
        method: 'POST',
        body: JSON.stringify({ inicio, fim, tipo_ausencia: tipo, observacao: observacao.trim() || undefined, ...quem }),
      });
      const f = frasesDaMarcacao(r, tipo);
      message.success(f.ok);
      if (f.aviso) message.warning(f.aviso, 8);
      setTipo(null); setObservacao('');
      await carregarLista();
      onMudou();
    } catch (e: any) {
      // A recusa do servidor já é frase para gente (dia com hora, cronômetro ligado, só fim de semana…).
      setErro(e?.message || 'Não foi possível marcar a ausência.');
    } finally { setEnviando(false); }
  }

  async function desfazer(a: AusenciaMarcada) {
    try {
      const r = await api('/financeiro/horas/ausencias/desfazer', {
        method: 'POST', body: JSON.stringify({ inicio: a.inicio, fim: a.fim, ...quem }),
      });
      const n = (r.desfeitos || []).length;
      message.success(n ? `Ausência desfeita (${n} ${n === 1 ? 'dia voltou' : 'dias voltaram'} a aceitar horas).` : 'Nada a desfazer nesse período.');
      await carregarLista();
      onMudou();
    } catch (e: any) { message.error(e?.message || 'Não foi possível desfazer.'); }
  }

  const atalhos = [
    { label: 'Hoje', value: [dayjs(hoje), dayjs(hoje)] as [Dayjs, Dayjs] },
    { label: 'Amanhã', value: [dayjs(hoje).add(1, 'day'), dayjs(hoje).add(1, 'day')] as [Dayjs, Dayjs] },
    { label: 'Semana que vem', value: [dayjs(hoje).day(8), dayjs(hoje).day(12)] as [Dayjs, Dayjs] },
  ];

  return (
    <Modal
      open={aberto} title="Folga ou férias" onCancel={onFechar} footer={<Button onClick={onFechar}>Fechar</Button>}
      width="94vw" style={{ maxWidth: 860, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto', overflowX: 'hidden' } }}
    >
      {emailAlvo && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={`Você está marcando a ausência de ${emailAlvo}.`} />
      )}
      <Row gutter={[24, 16]}>
        <Col xs={24} md={13}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Motivo</Text>
          <Radio.Group
            optionType="button" buttonStyle="solid" value={tipo}
            onChange={(e) => { setTipo(e.target.value); setErro(null); }}
            options={TIPOS_AUSENCIA}
          />

          <Text strong style={{ display: 'block', margin: '14px 0 6px' }}>Dias (do primeiro ao último)</Text>
          <RangePicker
            allowClear={false} format="DD/MM/YYYY" style={{ width: '100%' }}
            value={periodo} presets={atalhos}
            onChange={(v) => { if (v && v[0] && v[1]) { setPeriodo([v[0], v[1]]); setErro(null); } }}
          />
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
            {diasDeSemana > 0
              ? `${diasDeSemana} ${diasDeSemana === 1 ? 'dia de semana' : 'dias de semana'} no período. `
              : 'Esse período só tem fim de semana. '}
            Fim de semana e feriado ficam de fora (já não contam como falta), e dia com hora lançada também.
          </Text>

          <Text strong style={{ display: 'block', margin: '14px 0 6px' }}>Observação (opcional)</Text>
          <Input value={observacao} maxLength={200} onChange={(e) => setObservacao(e.target.value)} placeholder="ex.: consulta médica" />

          {erro && <Alert type="error" showIcon style={{ marginTop: 12 }} message={erro} />}
          <Button type="primary" block size="large" style={{ marginTop: 14 }} loading={enviando} onClick={marcar}>
            Marcar ausência
          </Button>
        </Col>

        <Col xs={24} md={11}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Ausências marcadas</Text>
          {marcadas === null && !erroLista && <Spin size="small" />}
          {erroLista && (
            <Alert type="warning" showIcon message="Não consegui ler as ausências marcadas" description={erroLista}
              action={<Button size="small" onClick={carregarLista}>Tentar de novo</Button>} />
          )}
          {marcadas && marcadas.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>Nenhuma ausência marcada dos últimos 30 dias em diante.</Text>
          )}
          {(marcadas || []).map((a) => (
            <div key={`${a.inicio}-${a.tipo_ausencia}-${a.do_rh}`} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
              padding: '6px 0', borderBottom: '1px solid #f0f0f0',
            }}>
              <span>
                <Tag color="blue" style={{ marginInlineEnd: 6 }}>{rotuloAusencia(a.tipo_ausencia)}</Tag>
                {periodoEmTexto(a)}
              </span>
              {a.do_rh ? (
                <Text type="secondary" style={{ fontSize: 11 }}>lançado pelo RH</Text>
              ) : (
                <Popconfirm
                  title="Desfazer esta ausência?" description="Os dias voltam a aceitar horas."
                  okText="Desfazer" cancelText="Não" onConfirm={() => desfazer(a)}
                >
                  <Button size="small" type="link" style={{ padding: 0 }}>desfazer</Button>
                </Popconfirm>
              )}
            </div>
          ))}
        </Col>
      </Row>
    </Modal>
  );
}
